import React, { FC, memo } from 'react';
import { useMutation } from 'react-query';
import {
  DeleteTwoTone,
  ExclamationCircleOutlined,
} from '@ant-design/icons/lib';
import { Button, message, Modal } from 'antd';
import { red } from '@ant-design/colors';
import { ProfileOwner } from 'models/user';
import { ButtonProps } from 'antd/lib/button';
import { UsersAPI } from 'api/UsersAPI';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { FormattedMessage } from 'react-intl';

export interface DeleteProfileOwnerProps
  extends Omit<ButtonProps, 'onClick' | 'loading' | 'href'> {
  profileOwner: ProfileOwner;
  onDeleted?: () => void;
}

export const DeleteProfileOwnerButton: FC<DeleteProfileOwnerProps> = memo(
  ({ profileOwner, onDeleted, ...buttonProps }) => {
    const [mutate, { isLoading }] = useMutation(UsersAPI.deleteProfileOwner);
    const intlMessage = useFormatMessage();

    const confirm = () =>
      Modal.confirm({
        title: intlMessage(
          { id: 'entity.delete.confirmation' },
          {
            name: `${profileOwner.firstName} ${profileOwner.lastName}`,
          }
        ),
        icon: <ExclamationCircleOutlined />,
        okText: intlMessage({ id: 'yes' }),
        cancelText: intlMessage({ id: 'cancel' }),
        okType: 'danger',
        onOk: () =>
          mutate(
            { userId: profileOwner.userId! },
            {
              onSuccess: () => {
                message.success(
                  intlMessage(
                    { id: 'entity.delete.success' },
                    {
                      name: `${profileOwner.firstName} ${profileOwner.lastName}`,
                    }
                  )
                );
                onDeleted && onDeleted();
              },
            }
          ),
      });

    return (
      <Button
        id="button-delete-profile-owner"
        loading={isLoading}
        onClick={() => confirm()}
        icon={<DeleteTwoTone twoToneColor={red.primary} />}
        {...buttonProps}
      >
        <FormattedMessage id="delete" tagName="span" />
      </Button>
    );
  }
);
